import { ReactNode, useMemo, useState, useEffect } from 'react';

import createSafeContext from '@paradigm/utils/src/createSafeContext';

import {
  useAggAsk,
  useAggBid,
  useMyAggAsks,
  useMyAggBids,
} from '#/unified-rfqs/ui/rfqs/quotes-contexts';
import { useGrfqRfq } from '#/unified-rfqs/ui/rfq-context';
import { useGrfqBbo } from '#/unified-rfqs/ui/rfqs/bbo';
import {
  minQuantityForProduct,
  normalizedMinBlockSize,
  strategyNotionalPrice,
} from '#/unified-rfqs/ui/rfqs/rfqs-functions';
import { findBaseProductCode, getProduct } from '#/products/domain';
import { Grfq, GrfqQuote } from '#/unified-rfqs/entities/grfq/domain';
import { useDefaultAccountsActions } from '#/preferences/default-accounts/context';

import { isStandardProduct } from '#/products/types';
import { CreateQuoteReport } from '../submit-quote';
import useSubmitAggressQuote from './useSubmitAggressQuote';
import { Status, SubmitCallback } from './types';

export interface AggressAction {
  readonly onChangeQuantity: (quantity: string) => void;
  readonly submitAggressQuote: (cb: SubmitCallback<CreateQuoteReport>) => void;
  readonly resetConfirmState: () => void;
  readonly quantity: string;
  readonly quantityDecimals: number;
  readonly isButtonEnabled: boolean;
  readonly isFieldEnabled: boolean;
  readonly status: Status;
  readonly lockedQuote: GrfqQuote | undefined;
}

export const [useAggressBuyAction, AggressBuyActionProvider] =
  createSafeContext<AggressAction>('AggressBuyAction');

export const [useAggressSellAction, AggressSellActionProvider] =
  createSafeContext<AggressAction>('AggressSellAction');

function getQuantityConfig(
  productCodes: Grfq['product_codes'],
  legs: Grfq['legs'],
  markPrice: number | null,
) {
  const productCode = productCodes[0];
  if (productCode == null) {
    return null;
  }

  /**
   * The values from `normalizedMinBlockSize` does not match correcty for BYB OPTIONS
   * products.
   * TODO: Refactor min block size function to match BYB OPTIONS
   */
  const product = getProduct(productCode);
  if (!isStandardProduct(product)) return null;
  if (product.venue === 'BYB' && product.kind === 'OPTION') {
    return {
      minQuantity: product.minContractIncrement,
      quantityDecimals: product.quantityPrecision,
    };
  }

  const minBlockValues = normalizedMinBlockSize(productCode, legs);
  const minIncrement = minBlockValues.minContractIncrement.toString();

  const [, dec = ''] = minIncrement.split('.');

  return {
    minQuantity: minQuantityForProduct(
      product,
      minBlockValues.minBlockSize,
      markPrice,
    ),
    quantityDecimals: dec.length,
  };
}

function useAggressForm(
  quote: GrfqQuote | undefined,
  isMyQuote: boolean,
  initialQty: string,
): AggressAction {
  const [quantity, setQuantity] = useState(initialQty);
  const rfq = useGrfqRfq();
  const { data: bbo } = useGrfqBbo();
  // Per-unit notional across legs, so a multi-leg spread is not collapsed
  // by the strategy mark (bbo.mark_price). Only affects products whose
  // minimum is notional-denominated (Deribit's USDC-linear futures);
  // minQuantityForProduct ignores the mark for everything else, coin
  // perps on Paradex and Bullish included.
  const markByInstrument = new Map(
    (bbo?.leg_prices ?? []).map((leg) => [
      leg.instrument,
      Number(leg.mark_price) || 0,
    ]),
  );
  const legsNotional = strategyNotionalPrice(
    rfq.legs.map((leg) => ({
      ratio: Number(leg.ratio) || 0,
      markPrice: markByInstrument.get(leg.instrument) ?? 0,
    })),
  );
  const markPrice =
    legsNotional > 0
      ? legsNotional
      : bbo?.mark_price != null
      ? Number(bbo.mark_price)
      : null;
  const { getDefaultAccount } = useDefaultAccountsActions();
  const { submitAggressQuote, status, lockedQuote, resetConfirmState } =
    useSubmitAggressQuote({
      quantity,
      rfq,
      quote,
    });

  useEffect(() => {
    const minBlockQty =
      getQuantityConfig(rfq.product_codes, rfq.legs, markPrice)?.minQuantity ??
      '';

    setQuantity((prevQuantity) =>
      prevQuantity !== '' ? prevQuantity : minBlockQty.toString(),
    );
  }, [rfq.product_codes, rfq.legs, markPrice]);

  const view = useMemo(() => {
    const isQuoteEmpty = quote === undefined;
    const minBlockValues = getQuantityConfig(
      rfq.product_codes,
      rfq.legs,
      markPrice,
    );

    const quantityDecimals = minBlockValues?.quantityDecimals ?? 0;
    const minBlockQty = minBlockValues?.minQuantity ?? 0;

    const productCode = findBaseProductCode(rfq.product_codes)!;
    const { venue } = getProduct(productCode);
    const isAccountValid = getDefaultAccount(venue, productCode) != null;
    const isOneClickFieldEnabled = !isMyQuote && !isQuoteEmpty;

    return {
      quantity,
      status,
      quantityDecimals,
      lockedQuote,
      isButtonEnabled:
        isOneClickFieldEnabled &&
        isAccountValid &&
        !['submitting', 'loading'].includes(status) &&
        Number(quantity || '0') >= minBlockQty,
      isFieldEnabled:
        isOneClickFieldEnabled &&
        !['confirming', 'submitting'].includes(status),
    };
  }, [
    quote,
    rfq.product_codes,
    rfq.legs,
    markPrice,
    getDefaultAccount,
    isMyQuote,
    quantity,
    status,
    lockedQuote,
  ]);

  const actions = useMemo(
    () => ({
      submitAggressQuote,
      resetConfirmState,
      onChangeQuantity: setQuantity,
    }),
    [resetConfirmState, submitAggressQuote],
  );

  return { ...view, ...actions };
}

interface Props {
  children: ReactNode;
  storedQty: string;
  setStoredQty: (value: string) => void;
}

export function AggressBuyProvider({
  children,
  storedQty,
  setStoredQty,
}: Props) {
  const aggAsk = useAggAsk();
  const aggMyAsks = useMyAggAsks();
  const isMyAsk = aggAsk != null && aggAsk.price === aggMyAsks[0]?.price;

  const value: AggressAction = useAggressForm(aggAsk, isMyAsk, storedQty);
  const valueWithStore: AggressAction = {
    ...value,
    onChangeQuantity: (qty: string) => {
      value.onChangeQuantity(qty);
      setStoredQty(qty);
    },
  };

  return (
    <AggressBuyActionProvider value={valueWithStore}>
      {children}
    </AggressBuyActionProvider>
  );
}

export function AggressSellProvider({
  children,
  storedQty,
  setStoredQty,
}: Props) {
  const aggBid = useAggBid();
  const aggMyBids = useMyAggBids();
  const isMyBid = aggBid != null && aggBid.price === aggMyBids[0]?.price;

  const value: AggressAction = useAggressForm(aggBid, isMyBid, storedQty);
  const valueWithStore: AggressAction = {
    ...value,
    onChangeQuantity: (qty: string) => {
      value.onChangeQuantity(qty);
      setStoredQty(qty);
    },
  };

  return (
    <AggressSellActionProvider value={valueWithStore}>
      {children}
    </AggressSellActionProvider>
  );
}
